import React from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { EPISODES, getEpisodeBySlug, PODCAST_SHOW } from "@/data/podcasts";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import RelatedContent from "@/components/RelatedContent";
import CTABlock from "@/components/CTABlock";

export const RadioIndex = () => {
  useSeo(SEO.radio);
  return (
    <main data-testid="radio-page" className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Radio Kostaja" }]} />
          <Overline color="yellow" className="mt-6 mb-3">{PODCAST_SHOW.name}</Overline>
          <h1 className="text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">
            Conversations on brand, business, and <span className="k-yellow">being easier to choose.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">{PODCAST_SHOW.tagline}</p>
        </div>
      </section>
      <section className="bg-black">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {EPISODES.map((e) => (
            <Link
              key={e.slug}
              to={`/radio/${e.slug}`}
              data-testid={`episode-${e.slug}`}
              className="group flex items-stretch border border-white/15 hover:k-hard-shadow-yellow"
            >
              <div className="w-20 lg:w-28 bg-[#FFC600] text-black flex items-center justify-center text-2xl lg:text-4xl font-black tracking-tighter border-r border-black/20">
                {String(e.number).padStart(2, "0")}
              </div>
              <div className="p-5 lg:p-7 flex-1">
                <Overline className="mb-2">Episode {e.number} · {e.duration}</Overline>
                <h2 className="text-xl lg:text-2xl font-black tracking-tighter leading-tight group-hover:text-[#FFC600]">{e.title}</h2>
                <p className="mt-2 text-sm text-white/70">{e.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <CTABlock variant="default" />
    </main>
  );
};

export const EpisodeDetail = () => {
  const { slug } = useParams();
  const ep = getEpisodeBySlug(slug);
  useSeo({ title: ep ? `Ep ${ep.number}. ${ep.title} | Radio Kostaja` : "Episode | Kostaja", description: ep?.summary || "" });
  if (!ep) {
    return (
      <main className="bg-black">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center">
          <h1 className="text-5xl font-black tracking-tighter">Episode not found.</h1>
          <div className="mt-8 flex justify-center gap-3">
            <SecondaryButton to="/radio">All episodes</SecondaryButton>
            <PrimaryButton to="/contact">Contact</PrimaryButton>
          </div>
        </div>
      </main>
    );
  }
  return (
    <main data-testid="episode-detail" className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { to: "/radio", label: "Radio Kostaja" }, { label: `Ep ${ep.number}` }]} />
          <Overline color="yellow" className="mt-6 mb-3">Episode {ep.number} · {ep.duration}</Overline>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">{ep.title}</h1>
          <p className="mt-5 max-w-2xl text-white/80 text-lg">{ep.summary}</p>
        </div>
      </section>
      <section className="bg-black border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="border border-white/15 p-8 lg:p-10 text-center">
            <Overline className="mb-3">Listen</Overline>
            <p className="text-white/70">
              Audio embed coming soon. Want the early link? <Link to="/contact" className="underline underline-offset-4">Send a note</Link>.
            </p>
            {/* DEV NOTE: Replace this block with the real Spotify embed when EPISODES[].embedUrl is set. */}
          </div>
        </div>
      </section>
      <RelatedContent
        serviceSlugs={ep.relatedServices || []}
        blogSlugs={ep.relatedBlog || []}
        heading="Take it further"
      />
      <CTABlock variant="podcast" />
    </main>
  );
};
